import { useGetTeam, useSetMemberAdminRole } from '@aninix/api'
import { useQueryClient } from '@tanstack/react-query'
import lowerCase from 'lodash/lowerCase'
import * as React from 'react'

import { ApiUser } from '@aninix/api/helpers'
import { toast } from '../../modules/toasts'
import { useCurrentUser } from '../../use-cases'
import { currentAdminRole } from './role'
import { useCurrentRowContext } from './use-current-row-context'

interface IProps {
  role: ApiUser['adminRole']
  selectedRole: ApiUser['adminRole']
  handleClose: (e?: any) => void
}

export const AdminRoleItem: React.FCC<IProps> = ({
  role,
  selectedRole,
  handleClose,
}: IProps) => {
  const setRole = useSetMemberAdminRole()

  const { teamId } = useCurrentUser()

  const { id, name, email } = useCurrentRowContext()

  const queryClient = useQueryClient()

  const getTeam = useGetTeam({ teamId: teamId! })

  const handleRoleClick = React.useCallback(() => {
    if (role === selectedRole) return

    queryClient.setQueryData(
      getTeam.queryKey,
      (oldData: typeof getTeam.data) => {
        const newData = { ...oldData }
        if (newData !== undefined) {
          newData.users!.find((user) => user.id === id)!.adminRole = role
        }
        return newData as typeof getTeam.data
      }
    )

    setTimeout(handleClose, 200)

    setRole
      .mutateAsync({ userId: id!, teamId: teamId!, adminRole: role })
      .then((value) => {
        getTeam.refetch()
        toast(
          <p>
            {`${name ?? email} is now `}
            <b>{lowerCase(currentAdminRole(role))}</b>
          </p>,
          { variant: 'success' }
        )
      })
      .catch(() => {
        toast("Couldn't change admin role", { variant: 'error' })
        getTeam.refetch()
      })
  }, [role, selectedRole])

  return (
    <div
      onClick={handleRoleClick}
      className="flex w-full min-w-[6rem] flex-row items-end gap-2 px-8 py-3 hover:bg-gray-100"
    >
      <p className="font-body text-base font-normal text-gray-500">
        {currentAdminRole(role)}
      </p>
      {role === selectedRole && <p className="text-gray-400">•</p>}
    </div>
  )
}

AdminRoleItem.displayName = 'AdminRoleItem'
